import { useAuthStore } from './store';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: any;
}

export async function apiRequest<T = any>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { apiKey, apiUrl } = useAuthStore.getState();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  if (apiKey) {
    headers['X-API-Key'] = apiKey;
  }

  const response = await fetch(`${apiUrl}${endpoint}`, {
    method: options.method || 'GET',
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'API request failed');
  }

  return data.data;
}

// Site endpoints
export const api = {
  // Site
  getSite: () => apiRequest('/v1/site'),

  // Locations
  getLocations: () => apiRequest('/v1/locations'),
  addLocation: (location: { city: string; state: string; country?: string }) =>
    apiRequest('/v1/locations', { method: 'POST', body: location }),
  expandToState: (state: string, country: string = 'USA') =>
    apiRequest('/v1/locations/expand-state', { method: 'POST', body: { state, country } }),
  getAvailableStates: () => apiRequest('/v1/locations/available-states'),

  // Categories
  getCategories: () => apiRequest('/v1/categories'),

  // Questions
  getQuestions: (params?: { location_id?: string; category_id?: string; status?: string; limit?: number; offset?: number }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) searchParams.set(key, String(value));
      });
    }
    return apiRequest(`/v1/questions?${searchParams.toString()}`);
  },
  getQuestion: (id: string) => apiRequest(`/v1/questions/${id}`),
  generateQuestions: (locationId: string, questionsPerCategory: number = 5) =>
    apiRequest('/v1/questions/generate', {
      method: 'POST',
      body: { location_id: locationId, questions_per_category: questionsPerCategory }
    }),
  deleteQuestion: (id: string) => apiRequest(`/v1/questions/${id}`, { method: 'DELETE' }),

  // Answers
  getAnswers: (params?: { status?: string; limit?: number; offset?: number }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) searchParams.set(key, String(value));
      });
    }
    return apiRequest(`/v1/answers?${searchParams.toString()}`);
  },
  getAnswer: (id: string) => apiRequest(`/v1/answers/${id}`),
  getAnswerByQuestion: (questionId: string) => apiRequest(`/v1/questions/${questionId}/answer`),
  generateAnswer: (questionId: string) =>
    apiRequest('/v1/answers/generate', { method: 'POST', body: { question_id: questionId } }),
  generateBulkAnswers: (questionIds: string[], concurrent: number = 3) =>
    apiRequest('/v1/answers/generate-bulk', { method: 'POST', body: { question_ids: questionIds, concurrent } }),
  publishAnswer: (id: string) =>
    apiRequest(`/v1/answers/${id}/publish`, { method: 'POST' }),

  // Render
  renderAnswer: (id: string, format: 'html' | 'json' = 'json') =>
    apiRequest(`/v1/render/${id}?format=${format}`),

  // Archives
  getLocationHub: (locationId: string) => apiRequest(`/v1/archives/location/${locationId}`),
  getCategoryHub: (locationId: string, categoryId: string) =>
    apiRequest(`/v1/archives/category/${locationId}/${categoryId}`),
  getSitemap: () => apiRequest('/v1/sitemap'),

  // Templates
  getTemplates: () => apiRequest('/v1/templates'),
  getTemplate: (slug: string) => apiRequest(`/v1/templates/${slug}`),

  // Analytics
  getAnalytics: (params?: { start_date?: string; end_date?: string }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) searchParams.set(key, String(value));
      });
    }
    return apiRequest(`/v1/analytics?${searchParams.toString()}`);
  }
};
